import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Checkbox, Input, Tabs, Textarea } from '@omnitok/ui';
import { FileSpreadsheet, List, Play, Upload } from 'lucide-react';
import { localizationService } from '../services/localization.service';
import type { LocalizationTargetLocale } from '../types/localization.types';

export interface LocalizationFormSubmission {
  mode: 'skus' | 'excel';
  skus?: string[];
  file?: File;
  sourceLocale: 'esCL';
  targetLocales: LocalizationTargetLocale[];
  force: boolean;
  replaceDefaultIndexado: boolean;
  additionalFields: string[];
}

interface LocalizationFormProps {
  onSubmit: (submission: LocalizationFormSubmission) => void;
  onResume: (runId: string) => void;
  isSubmitting: boolean;
}

const targetOptions: Array<{ value: LocalizationTargetLocale; label: string }> = [
  { value: 'esCO', label: 'Español Colombia (esCO)' },
  { value: 'esAR', label: 'Español Argentina (esAR)' },
  { value: 'esMX', label: 'Español México (esMX)' },
  { value: 'esPE', label: 'Español Perú (esPE)' },
  { value: 'esCAC', label: 'Español Centroamérica (esCAC)' },
  { value: 'ptBR', label: 'Português Brasil (ptBR)' },
];

const targetLocaleValues = new Set<LocalizationTargetLocale>(targetOptions.map(({ value }) => value));

function initialSkuText(): string {
  return new URLSearchParams(window.location.search).get('sku')?.trim().toUpperCase() ?? '';
}

function initialTargetLocales(): LocalizationTargetLocale[] {
  const requested = new URLSearchParams(window.location.search).get('target')?.trim();
  const normalized = requested?.toUpperCase() === 'PT' ? 'ptBR' : requested;
  return normalized && targetLocaleValues.has(normalized as LocalizationTargetLocale)
    ? [normalized as LocalizationTargetLocale]
    : ['esCO'];
}

export default function LocalizationForm({ onSubmit, onResume, isSubmitting }: LocalizationFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'skus' | 'excel'>('skus');
  const [skuText, setSkuText] = useState(initialSkuText);
  const [file, setFile] = useState<File | null>(null);
  const [targetLocales, setTargetLocales] = useState<LocalizationTargetLocale[]>(initialTargetLocales);
  const [replaceDefaultIndexado, setReplaceDefaultIndexado] = useState(false);
  const [directFields, setDirectFields] = useState<string[]>([]);
  const [maxAdditionalFields, setMaxAdditionalFields] = useState(100);
  const [maxTextLocalizationUnits, setMaxTextLocalizationUnits] = useState(100);
  const [maxExcelLocalizationUnits, setMaxExcelLocalizationUnits] = useState(1000);
  const [additionalFieldsText, setAdditionalFieldsText] = useState('');
  const [configError, setConfigError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [resumeRunId, setResumeRunId] = useState('');
  const additionalFields = useMemo(() => [...new Set(
    additionalFieldsText.split(/[\n,]+/).map((field) => field.trim()).filter(Boolean)
  )], [additionalFieldsText]);

  useEffect(() => {
    let active = true;
    void localizationService.getConfig().then((config) => {
      if (!active) return;
      setDirectFields(config.directLocalizableFields);
      setMaxAdditionalFields(config.maxAdditionalFields);
      setMaxTextLocalizationUnits(config.maxTextLocalizationUnitsPerRun ?? 100);
      setMaxExcelLocalizationUnits(config.maxExcelLocalizationUnitsPerRun ?? 1000);
    }).catch(() => {
      if (active) setConfigError('No fue posible cargar la lista de campos predeterminados.');
    });
    return () => { active = false; };
  }, []);

  const maxLocalizationUnits = mode === 'excel' ? maxExcelLocalizationUnits : maxTextLocalizationUnits;
  const skuLimit = targetLocales.length
    ? Math.floor(maxLocalizationUnits / targetLocales.length)
    : 0;

  const toggleTargetLocale = (targetLocale: LocalizationTargetLocale, checked: boolean) => {
    setTargetLocales((current) => checked
      ? [...new Set([...current, targetLocale])]
      : current.filter((locale) => locale !== targetLocale));
  };

  const submit = () => {
    setValidationError(null);
    if (!targetLocales.length) {
      setValidationError('Selecciona al menos una localización destino.');
      return;
    }
    if (additionalFields.length > maxAdditionalFields) {
      setValidationError(`Puedes agregar como máximo ${maxAdditionalFields} campos adicionales.`);
      return;
    }
    if (mode === 'skus') {
      const skus = [...new Set(skuText.split(/[\n,;]+/).map((sku) => sku.trim().toUpperCase()).filter(Boolean))];
      if (!skus.length) {
        setValidationError('Ingresa al menos un SKU.');
        return;
      }
      if (skus.length > skuLimit) {
        setValidationError(`Con ${targetLocales.length} localizaciones puedes enviar como máximo ${skuLimit} SKU.`);
        return;
      }
      onSubmit({ mode, skus, sourceLocale: 'esCL', targetLocales, force: false, replaceDefaultIndexado, additionalFields });
      return;
    }
    if (!file) {
      setValidationError('Selecciona un archivo Excel .xlsx.');
      return;
    }
    onSubmit({ mode, file, sourceLocale: 'esCL', targetLocales, force: false, replaceDefaultIndexado, additionalFields });
  };

  return (
    <div className="localization-form">
      <div className="localization-section-heading">
        <h2>Crear dry-run</h2>
        <p>Genera una previsualización revisable. Ningún modelo se modifica hasta aprobar y aplicar la corrida.</p>
      </div>

      <Tabs
        className="localization-mode-tabs"
        items={[
          { id: 'skus', label: 'Ingresar SKU', icon: <List size={16} /> },
          { id: 'excel', label: 'Subir Excel', icon: <FileSpreadsheet size={16} /> },
        ]}
        activeId={mode}
        onChange={(value) => setMode(value as 'skus' | 'excel')}
        variant="pills"
        size="sm"
      />

      {mode === 'skus' ? (
        <Textarea
          label="SKU"
          value={skuText}
          onChange={(event) => setSkuText(event.target.value)}
          rows={5}
          placeholder={'B9TR4LA\nOTROSKU'}
          helperText="Puedes separar los SKU por línea, coma o punto y coma."
          disabled={isSubmitting}
          fullWidth
        />
      ) : (
        <div className={`localization-file-picker${file ? ' localization-file-picker--selected' : ''}`}>
          <input
            ref={fileInputRef}
            className="localization-visually-hidden"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <Button
            type="button"
            variant="outline"
            leftIcon={<Upload size={17} />}
            disabled={isSubmitting}
            onClick={() => fileInputRef.current?.click()}
          >
            Seleccionar Excel
          </Button>
          <p>{file ? file.name : 'Primera hoja; columna SKU y URL Indexado, URL_Indexado o URL opcional.'}</p>
        </div>
      )}

      <div className="localization-target-selection">
        <strong>Localizaciones destino</strong>
        <div className="localization-target-checklist">
          {targetOptions.map((option) => (
            <Checkbox
              key={option.value}
              label={option.label}
              checked={targetLocales.includes(option.value)}
              onCheckedChange={(checked) => toggleTargetLocale(option.value, checked)}
              disabled={isSubmitting}
            />
          ))}
        </div>
        <Alert variant="info">
          Puedes enviar hasta {skuLimit} SKU con {targetLocales.length || 0} localización{targetLocales.length === 1 ? '' : 'es'} seleccionada{targetLocales.length === 1 ? '' : 's'}
          {' '}({skuLimit} × {targetLocales.length || 0} = {skuLimit * targetLocales.length} de {maxLocalizationUnits} unidades máximas).
        </Alert>
      </div>

      <Checkbox
        label="Reemplazar default indexado"
        checked={replaceDefaultIndexado}
        onCheckedChange={setReplaceDefaultIndexado}
        disabled={isSubmitting}
      />
      <p>Si ya existe un Indexado V5 para el país, guarda sus rows, pages y active en previous_translate y aplica la nueva traducción de esCL.</p>

      <details className="localization-fields-panel">
        <summary>
          <span>
            <strong>Campos directos a localizar</strong>
            <small>{directFields.length} predeterminados y {additionalFields.length} adicionales</small>
          </span>
        </summary>
        <div className="localization-fields-panel__body">
          {configError && <Alert variant="warning">{configError}</Alert>}
          <div>
            <h3>Predeterminados</h3>
            <div className="localization-badge-list localization-badge-list--scrollable">
              {directFields.map((field) => <Badge key={field} size="sm" variant="neutral">{field}</Badge>)}
            </div>
          </div>
          <Textarea
            label="Campos directos adicionales"
            value={additionalFieldsText}
            onChange={(event) => setAdditionalFieldsText(event.target.value)}
            rows={3}
            placeholder={'subtitulo\ndescripcionComercial'}
            helperText="Un nombre por línea o separado por coma. Se aplicará solamente a esta corrida."
            disabled={isSubmitting}
            fullWidth
          />
          {additionalFields.length > 0 && (
            <div className="localization-badge-list">
              {additionalFields.map((field) => <Badge key={field} size="sm" variant="primary">{field}</Badge>)}
            </div>
          )}
        </div>
      </details>

      {validationError && <Alert variant="warning">{validationError}</Alert>}

      <div>
        <Button
          type="button"
          variant="primary"
          leftIcon={<Play size={17} />}
          onClick={submit}
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creando corrida…' : 'Crear dry-run'}
        </Button>
      </div>

      <div className="localization-divider"><span>o retomar una corrida</span></div>
      <div className="localization-resume-row">
        <Input
          label="Run ID"
          placeholder="run_…"
          value={resumeRunId}
          onChange={(event) => setResumeRunId(event.target.value.trim())}
          disabled={isSubmitting}
          fullWidth
        />
        <Button
          type="button"
          variant="outline"
          disabled={isSubmitting || !resumeRunId}
          onClick={() => onResume(resumeRunId)}
        >
          Retomar corrida
        </Button>
      </div>
    </div>
  );
}
